/**
 * Chat Service - Handles all API calls related to the chat functionality
 */

// API URL from environment variables
const API_BASE_URL = process.env.REACT_APP_BACK_BASE_URL;
const siteName = 'samyama'; // Site identifier for API calls

/**
 * Send a message to the chat API
 * @param {string} message - Message to send
 * @param {boolean} cookiesAccepted - Whether functional cookies are accepted
 * @param {string|null} ephemeralThreadId - Thread ID for ephemeral conversations
 * @returns {Promise} Promise with the API response
 */
export const sendMessage = async (message, cookiesAccepted = true, ephemeralThreadId = null) => {
  try {
    // const timestamp = new Date().toISOString();
    // console.log(`[Chat DEBUG][${timestamp}] Sending message with consent status:`, cookiesAccepted);
    // console.log(`[Chat DEBUG][${timestamp}] Current document cookies:`, document.cookie);
    
    const endpoint = `${API_BASE_URL}/chat-api/${siteName}/assistant-chat`;
    // console.log(`[Chat DEBUG][${timestamp}] Sending to endpoint:`, endpoint);
    
    // IMPORTANT: We should trust the cookiesAccepted parameter from the React state
    // and not try to check document.cookie which can't see HttpOnly cookies
    
    const requestBody = {
      message,
      cookies_accepted: cookiesAccepted
    };

    // Include thread_id only for ephemeral conversations (when cookies are not accepted)
    if (!cookiesAccepted && ephemeralThreadId) {
      requestBody.thread_id = ephemeralThreadId;
      // console.log(`[Chat DEBUG][${timestamp}] Using ephemeral thread_id:`, ephemeralThreadId);
    }
    
    // console.log(`[Chat DEBUG][${timestamp}] Request body:`, requestBody);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30-second timeout for message sending
    
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody),
        credentials: 'include',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        console.error('[COOKIE_DEBUG] Message API returned non-OK status:', response.status);
        return {
          success: false,
          error: `Failed with status ${response.status}`
        };
      }
      
      const result = await response.json();
      // console.log('[COOKIE_DEBUG] Message response:', result);
      return result;
    } catch (fetchError) {
      clearTimeout(timeoutId);
      if (fetchError.name === 'AbortError') {
        console.error('[COOKIE_DEBUG] Message API request timed out');
        return {
          success: false,
          error: 'Request timed out'
        };
      }
      throw fetchError;
    }
  } catch (error) {
    console.error('Error sending message:', error);
    return {
      success: false,
      error: 'Failed to send message'
    };
  }
};

/**
 * Get chat ID from cookie
 * @returns {string|null} - The chat ID if found, null otherwise
 */
export const getChatId = () => {
  // Check cookies for chat ID
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const trimmed = cookie.trim();
    if (trimmed.startsWith('samyama_chat_id=')) {
      const value = trimmed.split('=')[1];
      // console.log('[COOKIE_DEBUG] Found chat ID in cookie:', value);
      return value;
    }
  }
  
  // console.log('[COOKIE_DEBUG] No chat ID found in cookie');
  return null;
};

/**
 * Initialize the chat and set the visitor cookie
 * @param {boolean} cookiesAccepted - Whether cookies are accepted by the user
 * @returns {Promise} - Promise with the initialization status
 */
export const initializeChat = async (cookiesAccepted = false) => {
  try {
    // Detailed cookie logging
    console.log('initializeChat called with consent:', cookiesAccepted);
    
    // IMPORTANT: Don't check for existing cookie by examining document.cookie
    // because HttpOnly cookies aren't visible to JavaScript
    
    // IMPORTANT: If cookies are NOT accepted, we should NOT try to set a cookie
    // Just return immediately with ephemeral flag
    if (!cookiesAccepted) {
      console.log('Cookies not accepted, using ephemeral chat only');
      return { success: true, ephemeral: true };
    }
    
    console.log('Initializing new chat session with cookies');
    // Add the cookies_accepted parameter to the URL
    const endpoint = `${API_BASE_URL}/chat-api/${siteName}/init?cookies_accepted=${cookiesAccepted}`;
    // console.log('Calling cookie initialization endpoint:', endpoint);
    
    // Use AbortController to ensure we don't hang indefinitely
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10-second timeout
    
    try {
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        credentials: 'include', // Important: include cookies
        cache: 'no-store', // Prevent caching of this request
        signal: controller.signal // Connect to our AbortController
      });
      
      // Log the full URL that was called
      // console.log('[COOKIE_DEBUG] API call URL:', response.url);
      
      clearTimeout(timeoutId); // Clear the timeout since the request completed
      
      if (!response.ok) {
        console.error('[COOKIE_DEBUG] Init API returned non-OK status:', response.status);
        return {
          success: false,
          error: `Failed with status ${response.status}`
        };
      }
      
      // Parse the response
      const data = await response.json();
      // console.log('[COOKIE_DEBUG] Init API response:', data);
      
      // Don't try to verify cookies by examining document.cookie
      // since HttpOnly cookies aren't visible to JavaScript
      if (data.success) {
        // console.log('Chat initialized successfully per server response');
      } else {
        console.error('Failed to initialize chat:', data.error);
      }
      
      return data;
    } catch (fetchError) {
      clearTimeout(timeoutId);
      if (fetchError.name === 'AbortError') {
        console.error('[COOKIE_DEBUG] Init API request timed out');
        return {
          success: false,
          error: 'Request timed out'
        };
      }
      throw fetchError; // Re-throw to be caught by the outer catch
    }
  } catch (error) {
    console.error('Error initializing chat:', error);
    return {
      success: false,
      error: 'Failed to initialize chat session'
    };
  }
};

// No need for separate cookie initialization - this is handled by the consent-manager

/**
 * Get conversation history from the backend
 * Only returns history if cookies are accepted and available
 * @param {boolean} cookiesAccepted - Whether cookies are accepted
 * @returns {Promise} Promise with conversation history
 */
export const getConversationHistory = async (cookiesAccepted = false) => {
  try {
    const endpoint = `${API_BASE_URL}/chat-api/${siteName}/conversation`;
    // console.log('Getting conversation history from:', endpoint);
    
    // If cookies are not accepted, don't even try to load history
    if (!cookiesAccepted) {
      console.log('Cookies not accepted, returning empty history');
      return {
        success: true,
        messages: [],
        ephemeral: true
      };
    }
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000); // 8-second timeout
    
    try {
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        console.error('[COOKIE_DEBUG] History API returned non-OK status:', response.status);
        return {
          success: false,
          error: `Failed with status ${response.status}`
        };
      }
      
      const result = await response.json();
      // console.log('[COOKIE_DEBUG] History response:', result);
      return result;
    } catch (fetchError) {
      clearTimeout(timeoutId);
      if (fetchError.name === 'AbortError') {
        console.error('[COOKIE_DEBUG] History API request timed out');
        return {
          success: false,
          error: 'Request timed out'
        };
      }
      throw fetchError;
    }
  } catch (error) {
    console.error('Error getting conversation history:', error);
    return {
      success: false,
      error: 'Failed to retrieve conversation history'
    };
  }
};

/**
 * Clear chat cookies via the clear-cookie endpoint
 * This is typically called when consent is revoked
 * @returns {Promise} - Promise with the clear cookie operation status
 */
export const clearChatCookies = async () => {
  try {
    console.log('[COOKIE_DEBUG] Explicitly clearing chat cookies');
    const endpoint = `${API_BASE_URL}/chat-api/${siteName}/clear-cookie`;
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache'
      },
      credentials: 'include'
    });
    
    if (!response.ok) {
      console.error('[COOKIE_DEBUG] Clear cookie API returned non-OK status:', response.status);
      return {
        success: false,
        error: `Failed with status ${response.status}`
      };
    }
    
    const result = await response.json();
    
    // Verify cookie was actually cleared
    const cookieAfterClear = document.cookie.split(';').find(
      item => item.trim().startsWith('samyama_chat_id=')
    );
    
    if (cookieAfterClear) {
      console.warn('[COOKIE_DEBUG] Cookie still present after clear operation!');
    } else {
      console.log('[COOKIE_DEBUG] Cookie successfully cleared');
    }
    
    return result;
  } catch (error) {
    console.error('Error clearing chat cookies:', error);
    return {
      success: false,
      error: 'Failed to clear chat cookies'
    };
  }
};